import { Formik, type FormikHelpers } from 'formik';
import { Alert, Box, Link } from '@gusto/workbench';
import { postJson } from 'requests/http';
import GoogleSignupCta from 'packages/signup/components/signupForm/googleSignupCta';
import { loginBaseUrl } from 'constants/lead_form_utils';
import trackSignInClicked from 'packages/signup/analytics/trackSignInClicked';
import validationSchema from 'components/accountant_leads/validation_schema';
import useAccountantLead from './useAccountantLead';
import type { AccountantLeadFormDetails } from './types';
import { usePartnerSignupContext } from './partnersSignupProvider';
import PartnerFormDetails from './partner_form_details';
import getSubmitPayload from './getSubmitPayload';
import {
  trackPartnerSurveySignupSubmitFailed,
  trackPartnerSurveySignupSubmitSuccess,
} from '../analytics/tracking_helpers';

const PartnerFormFooter = () => (
  <div className="margin-bottom-15px lp-footer-signin text-align-center">
    Already use Gusto?{' '}
    <Link onClick={trackSignInClicked} href={loginBaseUrl}>
      Sign in here
    </Link>
    .
  </div>
);

const PartnerForm = ({ ssoDomains = [] }: { ssoDomains?: string[] }) => {
  const { loading, fromGoogleSSO, accountantLead, formSubmitUrl } = useAccountantLead();
  const { signupGoals, runPayrollForClients, offerAdvisoryServices, perkGoals } =
    usePartnerSignupContext();

  if (loading) return null;

  const handleSubmit = (
    values: AccountantLeadFormDetails,
    formikOpts: FormikHelpers<AccountantLeadFormDetails>
  ) => {
    const surveyAnswers = {
      signupGoals,
      runPayrollForClients,
      offerAdvisoryServices,
      perkGoals,
    };

    const payload = getSubmitPayload(values, surveyAnswers);

    postJson(formSubmitUrl, payload)
      .then(res => {
        trackPartnerSurveySignupSubmitSuccess();
        window.location.href = res.redirect_url;
      })
      .catch(err => {
        const errorStatus = err?.cause?.status || '500';
        const errorMessage = err?.cause?.statusText || 'error from PartnerSignupForm';

        formikOpts.setStatus({ unknownError: true });
        formikOpts.setSubmitting(false);

        trackPartnerSurveySignupSubmitFailed(payload.accountant_lead, errorStatus, errorMessage);
      });
  };

  return (
    <Box maxWidth={632} width="100%">
      {!fromGoogleSSO && (
        <GoogleSignupCta
          googleSignupBaseUrl="/google_sso/accountant_signup/new"
          googleButtonLabel="Sign up with Google"
          fromPartner
        />
      )}

      <Formik
        initialValues={{
          accountant_lead: {
            first_name: accountantLead?.first_name || '',
            last_name: accountantLead?.last_name || '',
            email: accountantLead?.email || '',
            accounting_firm_name: accountantLead?.accounting_firm_name || '',
            website: '',
            number_companies_bucket: '',
            number_payroll_services_clients_bucket: '',
            phone: '',
          },
          terms_acceptance: false,
        }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema(true)}
      >
        {({ status }) => (
          <>
            <PartnerFormDetails ssoDomains={ssoDomains} />
            {status?.unknownError && (
              <Alert label="Error" severity="error">
                We&apos;re experiencing technical issues, Please try again later.
              </Alert>
            )}
          </>
        )}
      </Formik>

      <PartnerFormFooter />
    </Box>
  );
};

export default PartnerForm;
